import { SplineUtils } from '@/plugins/cad/module/utils'
import { EntityType } from '../../model/common/constant'
import type { IPoint } from '../../model/common/interface'
import type {
  ArcEntity,
  CircleEntity,
  EllipseEntity,
  IEntity,
  PolylineEntity,
  RayEntity,
  SplineEntity,
} from '../../model/entity'

/** 延伸目标端点：实体的起点或终点 */
export type ExtendEndType = 'start' | 'end'

// =========================================================================
// 基础几何工具函数
// =========================================================================

/** CCW 方向从 from 到 to 的角度距离，结果在 [0, 2π) */
function ccwAngleDist(from: number, to: number): number {
  const TWO_PI = Math.PI * 2
  return (((to - from) % TWO_PI) + TWO_PI) % TWO_PI
}

/** 归一化角度到 [0, 2π) */
function normalizeAngle(a: number): number {
  const TWO_PI = Math.PI * 2
  return ((a % TWO_PI) + TWO_PI) % TWO_PI
}

/**
 * 射线-线段交点。
 * 射线: origin + t * dir，t > 0
 * 线段: A + s * (B - A)，s ∈ [0, 1]
 * 返回 t（沿射线方向的距离），若无交点则返回 null。
 */
function raySegmentIntersection(origin: IPoint, dir: IPoint, A: IPoint, B: IPoint): number | null {
  const edgeDx = B.x - A.x
  const edgeDy = B.y - A.y
  // det = dir × edge = dir.x * edgeDy - dir.y * edgeDx（2D 叉积的 z 分量取反）
  const det = -(dir.x * edgeDy - dir.y * edgeDx)
  if (Math.abs(det) < 1e-12) return null // 平行

  const ox = A.x - origin.x
  const oy = A.y - origin.y
  // t = (ox * (-edgeDy) + oy * edgeDx) / det
  const t = (ox * -edgeDy + oy * edgeDx) / det
  // s = (dir.x * oy - dir.y * ox) / det
  const s = (dir.x * oy - dir.y * ox) / det
  if (t <= 1e-8 || s < -1e-8 || s > 1 + 1e-8) return null
  return t
}

/**
 * 圆-线段交点（返回落在线段上的点，最多 2 个）。
 * 圆: center, radius
 * 线段: A → B，s ∈ [0, 1]
 */
function circleSegmentIntersections(
  center: IPoint,
  radius: number,
  A: IPoint,
  B: IPoint
): IPoint[] {
  const dx = B.x - A.x
  const dy = B.y - A.y
  const vx = A.x - center.x
  const vy = A.y - center.y
  const a = dx * dx + dy * dy
  if (a < 1e-12) return []
  const b = 2 * (vx * dx + vy * dy)
  const c = vx * vx + vy * vy - radius * radius
  const disc = b * b - 4 * a * c
  if (disc < 0) return []
  const sqrtDisc = Math.sqrt(Math.max(0, disc))
  const points: IPoint[] = []
  for (const sign of [-1, 1] as const) {
    const s = (-b + sign * sqrtDisc) / (2 * a)
    if (s >= -1e-8 && s <= 1 + 1e-8) {
      points.push({ x: A.x + s * dx, y: A.y + s * dy })
    }
  }
  return points
}

/**
 * 由两端点和凸度（bulge）计算弧段参数。
 * bulge = tan(θ/4)，θ 为有向 CCW 圆心角。
 */
function arcParamsFromBulge(p1: IPoint, p2: IPoint, bulge: number) {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  const L = Math.hypot(dx, dy)
  if (L < 1e-10) return null
  const mx = (p1.x + p2.x) / 2
  const my = (p1.y + p2.y) / 2
  const nx = -dy / L
  const ny = dx / L
  const d_c = ((L / 2) * (1 - bulge * bulge)) / (2 * bulge)
  const cx = mx + nx * d_c
  const cy = my + ny * d_c
  const radius = Math.hypot(L / 2, d_c)
  const startAngle = Math.atan2(p1.y - cy, p1.x - cx)
  const endAngle = Math.atan2(p2.y - cy, p2.x - cx)
  return { cx, cy, radius, startAngle, endAngle }
}

/** 对凸度弧段采样，返回 numPts+1 个点 */
function sampleBulgeArc(p1: IPoint, p2: IPoint, bulge: number, numPts = 16): IPoint[] {
  const arc = arcParamsFromBulge(p1, p2, bulge)
  if (!arc) return [p1, p2]
  const { cx, cy, radius, startAngle } = arc
  const sweep = 4 * Math.atan(bulge)
  const pts: IPoint[] = []
  for (let i = 0; i <= numPts; i++) {
    const angle = startAngle + sweep * (i / numPts)
    pts.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) })
  }
  return pts
}

// =========================================================================
// ExtendUtils 主类
// =========================================================================

/**
 * 延伸实体工具集合，负责判断可延伸性、查找延伸交点与生成预览采样点。
 */
export class ExtendUtils {
  /** 可执行延伸操作的实体类型 */
  static readonly EnabledEntityTypes = new Set<EntityType>([
    EntityType.Polyline,
    EntityType.Arc,
    EntityType.Spline,
    EntityType.Ray,
  ])

  /** 可作为延伸边界的实体类型 */
  static readonly BoundaryEntityTypes = new Set<EntityType>([
    EntityType.Polyline,
    EntityType.Arc,
    EntityType.Circle,
    EntityType.Ellipse,
    EntityType.Spline,
    EntityType.Ray,
  ])

  /**
   * 判断实体是否支持延伸。
   *
   * @param entity - 目标实体
   * @returns 是否可被延伸
   */
  static isEntityExtendable(entity: IEntity): boolean {
    return (
      ExtendUtils.EnabledEntityTypes.has(entity.type) &&
      !(
        (entity.type === EntityType.Polyline || entity.type === EntityType.Spline) &&
        (entity as PolylineEntity | SplineEntity).isClosed
      )
    )
  }

  /**
   * 获取实体的两个端点（世界坐标）。
   * 仅支持 Polyline（开放）、Arc、Ray 与 Spline。
   *
   * @param entity - 目标实体
   * @returns 起点与终点；不支持或闭合时返回 null
   */
  static getEndPoints(entity: IEntity): { start: IPoint; end: IPoint } | null {
    switch (entity.type) {
      case EntityType.Polyline: {
        const poly = entity as PolylineEntity
        if (poly.isClosed || poly.vertices.length < 2) return null
        const v = poly.vertices
        return {
          start: { x: v[0].x, y: v[0].y },
          end: { x: v[v.length - 1].x, y: v[v.length - 1].y },
        }
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        return {
          start: {
            x: arc.center.x + arc.radius * Math.cos(arc.startAngle),
            y: arc.center.y + arc.radius * Math.sin(arc.startAngle),
          },
          end: {
            x: arc.center.x + arc.radius * Math.cos(arc.endAngle),
            y: arc.center.y + arc.radius * Math.sin(arc.endAngle),
          },
        }
      }
      case EntityType.Ray: {
        const ray = entity as RayEntity
        // 射线只有 startPoint 是有限端点；用一个远处虚拟点表示 ‘end’以支持就近判断逻辑
        const FAR = 1e5
        return {
          start: { x: ray.startPoint.x, y: ray.startPoint.y },
          end: {
            x: ray.startPoint.x + ray.unitVector.x * FAR,
            y: ray.startPoint.y + ray.unitVector.y * FAR,
          },
        }
      }
      case EntityType.Spline: {
        const spline = entity as SplineEntity
        if (spline.isClosed || spline.controlPoints.length < 2) return null
        // 标准夹紧 B 样条：cp[0] 就是曲线起点，cp[n-1] 就是终点
        const cp = spline.controlPoints
        return {
          start: { x: cp[0].x, y: cp[0].y },
          end: { x: cp[cp.length - 1].x, y: cp[cp.length - 1].y },
        }
      }
      default:
        return null
    }
  }

  /**
   * 返回离给定世界坐标最近的端点类型。
   *
   * @param entity - 目标实体
   * @param point - 参考点世界坐标
   * @returns 较近端点的类型；无可用端点时返回 null
   */
  static getNearestEnd(entity: IEntity, point: IPoint): ExtendEndType | null {
    const ends = ExtendUtils.getEndPoints(entity)
    if (!ends) return null
    const dStart = (point.x - ends.start.x) ** 2 + (point.y - ends.start.y) ** 2
    const dEnd = (point.x - ends.end.x) ** 2 + (point.y - ends.end.y) ** 2
    return dStart <= dEnd ? 'start' : 'end'
  }

  /**
   * 在 boundaries 中查找延伸目标端可接触到的第一个交点。
   *
   * @param entity - 被延伸的实体
   * @param endType - 要延伸的端点类型
   * @param boundaries - 参与求交的边界实体集合
   * @returns 最近的延伸交点；找不到时返回 null
   */
  static findExtendPoint(
    entity: IEntity,
    endType: ExtendEndType,
    boundaries: IEntity[]
  ): IPoint | null {
    switch (entity.type) {
      case EntityType.Polyline:
        return ExtendUtils.findPolylineExtendPoint(entity as PolylineEntity, endType, boundaries)
      case EntityType.Arc:
        return ExtendUtils.findArcExtendPoint(entity as ArcEntity, endType, boundaries)
      case EntityType.Ray:
        return ExtendUtils.findRayExtendPoint(entity as RayEntity, endType, boundaries)
      case EntityType.Spline:
        return ExtendUtils.findSplineExtendPoint(entity as SplineEntity, endType, boundaries)
      default:
        return null
    }
  }

  /**
   * 计算延伸预览的采样点序列：直线段直接返回两端点，圆弧沿弧线采样若干点。
   *
   * @param entity - 被延伸的实体
   * @param endType - 要延伸的端点类型
   * @param intersectionPoint - 延伸到的目标交点
   * @returns 用于绘制预览的采样点序列；不支持的延伸返回空数组
   */
  static getExtendPreviewPoints(
    entity: IEntity,
    endType: ExtendEndType,
    intersectionPoint: IPoint
  ): IPoint[] {
    // 射线的 'end' 为无穷方向，不支持延伸
    if (entity.type === EntityType.Ray && endType === 'end') return []
    if (entity.type === EntityType.Arc) {
      const arc = entity as ArcEntity
      const fromAngle = endType === 'end' ? arc.endAngle : arc.startAngle
      const toAngle = Math.atan2(
        intersectionPoint.y - arc.center.y,
        intersectionPoint.x - arc.center.x
      )
      return ExtendUtils.sampleArcExtension(arc, fromAngle, toAngle, endType, 24)
    }
    // Polyline：返回端点到交点的两点折线
    const ends = ExtendUtils.getEndPoints(entity)
    if (!ends) return []
    const startPt = endType === 'end' ? ends.end : ends.start
    return [startPt, intersectionPoint]
  }

  // =========================================================================
  // 私有: Polyline 延伸
  // =========================================================================

  private static findPolylineExtendPoint(
    entity: PolylineEntity,
    endType: ExtendEndType,
    boundaries: IEntity[]
  ): IPoint | null {
    const verts = entity.vertices
    if (verts.length < 2) return null

    let origin: IPoint
    let dir: IPoint

    if (endType === 'end') {
      const v1 = verts[verts.length - 2]
      const v2 = verts[verts.length - 1]
      const len = Math.hypot(v2.x - v1.x, v2.y - v1.y)
      if (len < 1e-10) return null
      origin = { x: v2.x, y: v2.y }
      dir = { x: (v2.x - v1.x) / len, y: (v2.y - v1.y) / len }
    } else {
      const v0 = verts[0]
      const v1 = verts[1]
      const len = Math.hypot(v0.x - v1.x, v0.y - v1.y)
      if (len < 1e-10) return null
      origin = { x: v0.x, y: v0.y }
      dir = { x: (v0.x - v1.x) / len, y: (v0.y - v1.y) / len }
    }

    return ExtendUtils.findNearestRayIntersection(origin, dir, boundaries, entity)
  }

  // =========================================================================
  // 私有: Arc 延伸
  // =========================================================================

  private static findArcExtendPoint(
    entity: ArcEntity,
    endType: ExtendEndType,
    boundaries: IEntity[]
  ): IPoint | null {
    const { center, radius, clockwise } = entity
    const arcStart = normalizeAngle(entity.startAngle)
    const arcEnd = normalizeAngle(entity.endAngle)

    // 弧已扫过的角度（同方向），缺口大小 = 2π - arcSpan
    // 延伸候选点的 dist 必须严格小于缺口大小，避免把弧体内的另一侧圆-边界交点当成目标
    const arcSpan = !clockwise
      ? ccwAngleDist(arcStart, arcEnd) // CCW 弧扫角
      : ccwAngleDist(arcEnd, arcStart) // CW 弧扫角
    const gapSize = Math.PI * 2 - arcSpan

    let bestPt: IPoint | null = null
    let bestDist = Infinity

    for (const boundary of boundaries) {
      if (boundary === entity) continue
      const segs = ExtendUtils.getBoundarySegments(boundary)
      for (const [A, B] of segs) {
        const intersections = circleSegmentIntersections(center, radius, A, B)
        for (const pt of intersections) {
          const angle = normalizeAngle(Math.atan2(pt.y - center.y, pt.x - center.x))
          let dist: number
          if (!clockwise) {
            // CCW 弧
            if (endType === 'end') {
              // 从 arcEnd 沿 CCW 方向延伸
              dist = ccwAngleDist(arcEnd, angle)
            } else {
              // 从 arcStart 沿 CW 方向延伸（= CCW 从 angle 到 arcStart）
              dist = ccwAngleDist(angle, arcStart)
            }
          } else {
            // CW 弧
            if (endType === 'end') {
              // 从 arcEnd 沿 CW 方向延伸（= CCW 从 angle 到 arcEnd）
              dist = ccwAngleDist(angle, arcEnd)
            } else {
              // 从 arcStart 沿 CCW 方向延伸
              dist = ccwAngleDist(arcStart, angle)
            }
          }
          // dist > 1e-6 : 排除当前端点本身（浮点误差）
          // dist < gapSize : 排除弧体内部及另一侧的交点（圆与线段最多 2 个交点，
          //   另一交点的 dist 通常接近 2π - arcSpan，即 gapSize，会在此处被过滤）
          if (dist > 1e-6 && dist < gapSize && dist < bestDist) {
            bestDist = dist
            bestPt = pt
          }
        }
      }
    }

    return bestPt
  }

  // =========================================================================
  // 私有: 弧段预览采样
  // =========================================================================

  private static sampleArcExtension(
    arc: ArcEntity,
    fromAngle: number,
    toAngle: number,
    endType: ExtendEndType,
    numPts: number
  ): IPoint[] {
    const { center, radius, clockwise } = arc
    let sweep: number
    if (!clockwise) {
      sweep =
        endType === 'end'
          ? ccwAngleDist(fromAngle, toAngle) // CCW 延伸
          : -ccwAngleDist(toAngle, fromAngle) // CW 延伸
    } else {
      sweep =
        endType === 'end'
          ? -ccwAngleDist(toAngle, fromAngle) // CW 延伸
          : ccwAngleDist(fromAngle, toAngle) // CCW 延伸
    }
    if (Math.abs(sweep) < 1e-6) return []
    const pts: IPoint[] = []
    for (let i = 0; i <= numPts; i++) {
      const angle = fromAngle + sweep * (i / numPts)
      pts.push({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) })
    }
    return pts
  }

  // =========================================================================
  // 私有: Ray 延伸
  // =========================================================================

  /**
   * 射线延伸：只支持 'start'。
   * 延伸方向为 -unitVector（往起点背后延伸）。
   */
  private static findRayExtendPoint(
    entity: RayEntity,
    endType: ExtendEndType,
    boundaries: IEntity[]
  ): IPoint | null {
    if (endType !== 'start') return null // 射线的 end 为无穷，无法延伸
    const { startPoint, unitVector } = entity
    const dir = { x: -unitVector.x, y: -unitVector.y }
    return ExtendUtils.findNearestRayIntersection(
      { x: startPoint.x, y: startPoint.y },
      dir,
      boundaries,
      entity
    )
  }

  // =========================================================================
  // 私有: Spline 延伸
  // =========================================================================

  /**
   * 样条延伸：以端点切线方向投射射线，找到最近的边界交点。
   * 对于标准夹紧 B 样条：
   *   起点切线方向 = cp[0] → cp[1] 的反方向（延伸到起点后方）
   *   终点切线方向 = cp[n-2] → cp[n-1]（延伸到终点前方）
   */
  private static findSplineExtendPoint(
    entity: SplineEntity,
    endType: ExtendEndType,
    boundaries: IEntity[]
  ): IPoint | null {
    const cp = entity.controlPoints
    if (cp.length < 2) return null

    let origin: IPoint
    let dir: IPoint

    if (endType === 'end') {
      const p1 = cp[cp.length - 2]
      const p2 = cp[cp.length - 1]
      const len = Math.hypot(p2.x - p1.x, p2.y - p1.y)
      if (len < 1e-10) return null
      origin = { x: p2.x, y: p2.y }
      dir = { x: (p2.x - p1.x) / len, y: (p2.y - p1.y) / len }
    } else {
      const p0 = cp[0]
      const p1 = cp[1]
      const len = Math.hypot(p0.x - p1.x, p0.y - p1.y)
      if (len < 1e-10) return null
      origin = { x: p0.x, y: p0.y }
      // 延伸方向为 cp[1]→cp[0] 的方向（背向延伸）
      dir = { x: (p0.x - p1.x) / len, y: (p0.y - p1.y) / len }
    }

    return ExtendUtils.findNearestRayIntersection(origin, dir, boundaries, entity)
  }

  // =========================================================================
  // 私有: 射线求最近交点
  // =========================================================================

  private static findNearestRayIntersection(
    origin: IPoint,
    dir: IPoint,
    boundaries: IEntity[],
    exclude: IEntity
  ): IPoint | null {
    let bestT = Infinity
    let bestPt: IPoint | null = null

    for (const boundary of boundaries) {
      if (boundary === exclude) continue
      const segs = ExtendUtils.getBoundarySegments(boundary)
      for (const [A, B] of segs) {
        const t = raySegmentIntersection(origin, dir, A, B)
        if (t !== null && t > 1e-8 && t < bestT) {
          bestT = t
          bestPt = { x: origin.x + t * dir.x, y: origin.y + t * dir.y }
        }
      }
    }

    return bestPt
  }

  // =========================================================================
  // 私有: 将边界实体离散成线段对
  // =========================================================================

  private static getBoundarySegments(entity: IEntity): Array<[IPoint, IPoint]> {
    const segs: Array<[IPoint, IPoint]> = []

    switch (entity.type) {
      case EntityType.Polyline: {
        const poly = entity as PolylineEntity
        const v = poly.vertices
        for (let i = 0; i < v.length - 1; i++) {
          const bulge = v[i].bulge ?? 0
          if (Math.abs(bulge) < 1e-10) {
            segs.push([
              { x: v[i].x, y: v[i].y },
              { x: v[i + 1].x, y: v[i + 1].y },
            ])
          } else {
            const pts = sampleBulgeArc(
              { x: v[i].x, y: v[i].y },
              { x: v[i + 1].x, y: v[i + 1].y },
              bulge,
              16
            )
            for (let j = 0; j < pts.length - 1; j++) {
              segs.push([pts[j], pts[j + 1]])
            }
          }
        }
        if (poly.isClosed && v.length > 1) {
          const last = v[v.length - 1]
          const first = v[0]
          segs.push([
            { x: last.x, y: last.y },
            { x: first.x, y: first.y },
          ])
        }
        break
      }
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        const pts = ExtendUtils.sampleArc(arc, 48)
        for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]])
        break
      }
      case EntityType.Circle: {
        const circle = entity as CircleEntity
        const pts = ExtendUtils.sampleCircle(circle, 64)
        for (let i = 0; i < pts.length; i++) {
          segs.push([pts[i], pts[(i + 1) % pts.length]])
        }
        break
      }
      case EntityType.Ellipse: {
        const ell = entity as EllipseEntity
        const pts = ExtendUtils.sampleEllipse(ell, 64)
        for (let i = 0; i < pts.length; i++) {
          segs.push([pts[i], pts[(i + 1) % pts.length]])
        }
        break
      }
      case EntityType.Spline: {
        const spl = entity as SplineEntity
        const pts = ExtendUtils.sampleSpline(spl, 64)
        for (let i = 0; i < pts.length - 1; i++) segs.push([pts[i], pts[i + 1]])
        break
      }
      case EntityType.Ray: {
        const ray = entity as RayEntity
        const FAR = 1e5
        segs.push([
          { x: ray.startPoint.x, y: ray.startPoint.y },
          {
            x: ray.startPoint.x + ray.unitVector.x * FAR,
            y: ray.startPoint.y + ray.unitVector.y * FAR,
          },
        ])
        break
      }
    }

    return segs
  }

  // =========================================================================
  // 私有: 实体采样辅助
  // =========================================================================

  private static sampleArc(arc: ArcEntity, numPts: number): IPoint[] {
    const { center, radius, startAngle, clockwise } = arc
    // 弧的 CCW 扫角（从 startAngle 到 endAngle）
    const sweep = clockwise
      ? -ccwAngleDist(arc.endAngle, arc.startAngle) // CW → 负扫角
      : ccwAngleDist(arc.startAngle, arc.endAngle) // CCW → 正扫角
    // 确保全圆弧也能采样
    const actualSweep = sweep === 0 ? Math.PI * 2 * (clockwise ? -1 : 1) : sweep
    const pts: IPoint[] = []
    for (let i = 0; i <= numPts; i++) {
      const angle = startAngle + actualSweep * (i / numPts)
      pts.push({ x: center.x + radius * Math.cos(angle), y: center.y + radius * Math.sin(angle) })
    }
    return pts
  }

  private static sampleCircle(circle: CircleEntity, numPts: number): IPoint[] {
    const pts: IPoint[] = []
    for (let i = 0; i < numPts; i++) {
      const angle = (i / numPts) * Math.PI * 2
      pts.push({
        x: circle.center.x + circle.radius * Math.cos(angle),
        y: circle.center.y + circle.radius * Math.sin(angle),
      })
    }
    return pts
  }

  private static sampleEllipse(ell: EllipseEntity, numPts: number): IPoint[] {
    const rX = ell.radiusX
    const rY = ell.radiusY
    const rot = ell.rotation
    const cosR = Math.cos(rot)
    const sinR = Math.sin(rot)
    const { startAngle, endAngle, clockwise, center } = ell
    const sweep = clockwise
      ? -ccwAngleDist(endAngle, startAngle)
      : ccwAngleDist(startAngle, endAngle)
    const actualSweep = sweep === 0 ? Math.PI * 2 * (clockwise ? -1 : 1) : sweep
    const pts: IPoint[] = []
    for (let i = 0; i <= numPts; i++) {
      const t = startAngle + actualSweep * (i / numPts)
      const lx = rX * Math.cos(t)
      const ly = rY * Math.sin(t)
      pts.push({
        x: center.x + lx * cosR - ly * sinR,
        y: center.y + lx * sinR + ly * cosR,
      })
    }
    return pts
  }

  private static sampleSpline(spl: SplineEntity, numPts: number): IPoint[] {
    if (!spl.knots || !spl.controlPoints || spl.degree == null) return []
    const uMin = spl.knots[spl.degree] ?? 0
    const uMax = (spl.knots[spl.knots.length - spl.degree - 1] ?? 1) - 1e-9
    const pts: IPoint[] = []
    for (let i = 0; i <= numPts; i++) {
      const u = uMin + (uMax - uMin) * (i / numPts)
      const pt = SplineUtils.evaluateBSpline(u, spl.controlPoints, spl.knots, spl.degree)
      pts.push({ x: pt.x, y: pt.y })
    }
    return pts
  }
}
